/* ─────────────────────────────────────────────────────
   Varali Cart Drawer — cart-drawer.js
───────────────────────────────────────────────────── */

console.log('[Varali] cart-drawer.js loaded');

/* ── Config ─────────────────────────────────────── */

/*
  TIER SYSTEM
  -----------
  Thresholds are in paise (1/100 of a rupee). Divide by 100 for the rupee value:
    250000 paise = ₹2,500  → tier 1 reward: ₹150 off
    500000 paise = ₹5,000  → tier 2 reward: ₹300 off
    750000 paise = ₹7,500  → tier 3 reward: ₹100 off  (this is also the max for the bar)

  Progress bar: (cart.total_price / last_threshold) * 100, capped at 100%.
    e.g. cart ₹3,000 → (300000 / 750000) * 100 = 40%

  To add or edit a tier: add/change a { threshold, reward } object.
  The last threshold is always treated as 100% of the progress bar.

  CROSS-SELL LOGIC
  ----------------
  vcdCrossSell() first tries Shopify's recommendations API:
    /recommendations/products.json?product_id=<first_item_id>&limit=8&intent=related
  It filters out products already in the cart, then shows the first 3 results.

  If the API returns nothing (e.g. not enough Shopify recommendation data), it falls
  back to vcdCrossSellHandles(), which fetches the first product from VCD_CROSS_SELL
  that isn't already in the cart.

  To change fallback cross-sell products: edit VCD_CROSS_SELL below with the
  Shopify product handle (the slug in the product URL, e.g. "my-product-name").
*/

const VCD_TIERS = [
  { threshold:  250000, reward: '₹150 off' },
  { threshold:  500000, reward: '₹300 off' },
  { threshold: 1000000, reward: '₹500 off' },
];

// Fallback cross-sell handles — only used when Shopify recommendations returns nothing
const VCD_CROSS_SELL = [
  'aambal-embroidery-shirt',
  'bhoomika-shirt',
  'kuttikalam-shirt',
];

/* ── State ──────────────────────────────────────── */
let _open             = false;
let _fetching         = false;
let _discount         = sessionStorage.getItem('vcd_discount') || '';
let _prevRewardTotal  = -1; // -1 = not yet initialized; prevents false celebrate on first render

/* ── Helpers ────────────────────────────────────── */
function vcdFmt(cents) {
  return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
}

function vcdEsc(s) {
  return String(s || '').replace(/[&<>"']/g, c =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])
  );
}

function vcdImg(src, w) {
  if (!src) return '';
  if (src.startsWith('//')) src = 'https:' + src;
  return src + (src.includes('?') ? '&' : '?') + 'width=' + w;
}

const vcd$ = id => document.getElementById(id);

/* ── Force the drawer above EVERYTHING ──────────────
   Moves the drawer + overlay to be the last children of
   <body> (escapes any stacking-context wrapper) and sets
   positioning via inline styles, which beat any stylesheet
   — even a stale/cached cart-drawer.css. We deliberately do
   NOT set `transform` here so the open/close CSS animation
   still works. */
function vcdForceFront() {
  const drawer  = vcd$('varali-cart-drawer');
  const overlay = vcd$('varali-cart-overlay');

  if (overlay && overlay.parentNode !== document.body) document.body.appendChild(overlay);
  if (drawer  && drawer.parentNode  !== document.body) document.body.appendChild(drawer);

  if (overlay) {
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.zIndex = '2147483646';
  }
  if (drawer) {
    const mobile = window.innerWidth <= 480;
    drawer.style.position = 'fixed';
    drawer.style.top = '0';
    drawer.style.bottom = '0';
    drawer.style.right = '0';
    drawer.style.left = mobile ? '12%' : 'auto';
    drawer.style.width = mobile ? '88%' : '';
    drawer.style.borderRadius = mobile ? '16px 0 0 16px' : '';
    drawer.style.height = '100vh';
    drawer.style.margin = '0';
    drawer.style.zIndex = '2147483647';
  }
}

/* ── Open / Close ───────────────────────────────── */
function vcdOpen() {
  const drawer  = vcd$('varali-cart-drawer');
  const overlay = vcd$('varali-cart-overlay');
  if (!drawer) { console.warn('[Varali] drawer element not found'); return; }

  vcdForceFront();   // pin to body + force z-index before showing

  _open = true;
  drawer.classList.add('vcd-open');
  drawer.setAttribute('aria-hidden', 'false');
  if (overlay) { overlay.classList.add('vcd-open'); overlay.setAttribute('aria-hidden', 'false'); }
  document.body.style.overflow = 'hidden';
  vcdFetch();
}

function vcdClose() {
  const drawer  = vcd$('varali-cart-drawer');
  const overlay = vcd$('varali-cart-overlay');
  if (!drawer) return;
  _open = false;
  drawer.classList.remove('vcd-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (overlay) { overlay.classList.remove('vcd-open'); overlay.setAttribute('aria-hidden', 'true'); }
  document.body.style.overflow = '';
}

/* ── Fetch cart ─────────────────────────────────── */
function vcdFetch() {
  if (_fetching) return;
  _fetching = true;
  const loading = vcd$('vcd-loading');
  const items   = vcd$('vcd-items');
  if (loading) loading.style.display = 'flex';
  if (items)   items.style.display   = 'none';

  fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then(r => r.json())
    .then(cart => { _fetching = false; vcdRender(cart); })
    .catch(err => { _fetching = false; console.warn('[Varali] fetch error', err); });
}

/* ── Render ─────────────────────────────────────── */
function vcdRender(cart) {
  const loading      = vcd$('vcd-loading');
  const itemsEl      = vcd$('vcd-items');
  const emptyEl      = vcd$('vcd-empty');
  const footerEl     = vcd$('vcd-footer');
  const crosssellEl  = vcd$('vcd-crosssell');

  if (loading) loading.style.display = 'none';

  vcdUpdateCount(cart.item_count);
  vcdRewardBar(cart.total_price);

  if (cart.item_count === 0) {
    if (itemsEl)     { itemsEl.innerHTML = ''; itemsEl.style.display = 'none'; }
    if (footerEl)    footerEl.style.display = 'none';
    if (crosssellEl) crosssellEl.style.display = 'none';
    if (emptyEl)     emptyEl.classList.add('show');
    return;
  }

  if (emptyEl)  emptyEl.classList.remove('show');
  if (footerEl) footerEl.style.display = 'block';

  if (itemsEl) {
    itemsEl.innerHTML = '';
    cart.items.forEach(item => itemsEl.appendChild(vcdBuildItem(item)));
    itemsEl.style.display = 'block';
  }

  vcdSubtotal(cart);
  vcdCrossSell(cart);
  vcdRestoreDiscount();
}

/* ── Build item ─────────────────────────────────── */
function vcdBuildItem(item) {
  const div = document.createElement('div');
  div.className = 'vcd-item';
  div.dataset.key = item.key;

  const hasDisc = item.original_line_price > item.final_line_price;
  const saving  = item.original_price - item.final_price;
  const wasTag  = hasDisc ? `<span class="vcd-item-was">${vcdFmt(item.original_price)}</span>` : '';
  const saveTag = (hasDisc && saving > 0) ? `<span class="vcd-item-save">Save ${vcdFmt(saving)}</span>` : '';
  const varTag  = item.variant_title && item.variant_title !== 'Default Title'
    ? `<div class="vcd-item-variant">${vcdEsc(item.variant_title)}</div>` : '';

  /* Back-link to the PDP so a shopper can re-check description, sizes, images
     or material without leaving the cart the long way round.

     item.url is Shopify's own line-item URL from /cart.js and already carries
     the chosen variant (/products/handle?variant=123), so the PDP opens on the
     size they actually added. Never build this by hand. Guarded because a line
     for a deleted product can come back without one.

     Only the image and the title are linked — never the whole row — so the
     quantity buttons and the remove button keep their own hit areas. */
  const url = item.url ? vcdEsc(item.url) : '';

  const imgTag = `<img class="vcd-item-img"
         src="${vcdEsc(vcdImg(item.image, 80))}"
         alt="${vcdEsc(item.product_title)}"
         loading="lazy" width="80" height="96"
         onerror="this.style.visibility='hidden'">`;

  /* The image link is hidden from assistive tech and skipped by Tab: it points
     at the same place as the title right beside it, so exposing both would
     announce every line item twice. Same pattern Dawn uses on the cart page. */
  const imgHtml = url
    ? `<a class="vcd-item-img-link" href="${url}" tabindex="-1" aria-hidden="true">${imgTag}</a>`
    : imgTag;

  const nameHtml = url
    ? `<a class="vcd-item-name-link" href="${url}">${vcdEsc(item.product_title)}</a>`
    : vcdEsc(item.product_title);

  div.innerHTML = `
    ${imgHtml}
    <div class="vcd-item-body">
      <div class="vcd-item-name">${nameHtml}</div>
      ${varTag}
      <div class="vcd-item-price-row">
        <span class="vcd-item-price">${vcdFmt(item.final_price)}</span>
        ${wasTag}${saveTag}
      </div>
      <div class="vcd-item-bottom">
        <div class="vcd-qty">
          <button class="vcd-qty-btn" data-act="dec" aria-label="Decrease">−</button>
          <span class="vcd-qty-num">${item.quantity}</span>
          <button class="vcd-qty-btn" data-act="inc" aria-label="Increase">+</button>
        </div>
        <button class="vcd-delete" aria-label="Remove">🗑</button>
      </div>
    </div>`;

  const key = item.key;
  let   qty = item.quantity;

  div.querySelector('[data-act="dec"]').addEventListener('click', () => vcdQty(key, qty - 1, div));
  div.querySelector('[data-act="inc"]').addEventListener('click', () => vcdQty(key, qty + 1, div));
  div.querySelector('.vcd-delete').addEventListener('click',      () => vcdRemove(key, div));
  return div;
}

/* ── Qty / Remove ───────────────────────────────── */
function vcdSetBusy(el, busy) {
  el.querySelectorAll('.vcd-qty-btn,.vcd-delete').forEach(b => b.disabled = busy);
}

function vcdAnimOut(el, cb) {
  const h = el.offsetHeight;
  Object.assign(el.style, {
    transition: 'opacity .2s,transform .2s,max-height .35s,padding .35s',
    overflow: 'hidden', maxHeight: h + 'px', opacity: '0', transform: 'translateX(16px)',
  });
  setTimeout(() => { el.style.maxHeight = '0'; el.style.paddingTop = '0'; el.style.paddingBottom = '0'; }, 160);
  setTimeout(() => { el.remove(); cb(); }, 420);
}

function vcdQty(key, newQty, el) {
  if (newQty < 0) return;
  vcdSetBusy(el, true);
  if (newQty === 0) { vcdAnimOut(el, () => vcdPost(key, 0)); return; }
  vcdPost(key, newQty, () => vcdSetBusy(el, false));
}

function vcdRemove(key, el) {
  vcdSetBusy(el, true);
  vcdAnimOut(el, () => vcdPost(key, 0));
}

function vcdPost(key, qty, done) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty }),
  })
    .then(r => r.json())
    .then(cart => { vcdRender(cart); done && done(); })
    .catch(() => { done ? done() : vcdFetch(); });
}

/* ── Subtotal ────────────────────────────────────── */
function vcdSubtotal(cart) {
  const total  = cart.total_price;
  const orig   = cart.original_total_price;
  const saved  = orig > total ? orig - total : 0;
  const nowEl  = vcd$('vcd-sub-now');
  const wasEl  = vcd$('vcd-sub-was');
  const badge  = vcd$('vcd-savings-badge');

  if (nowEl) nowEl.textContent = vcdFmt(total);
  if (wasEl) { wasEl.textContent = saved > 0 ? vcdFmt(orig) : ''; wasEl.style.display = saved > 0 ? '' : 'none'; }
  if (badge) { badge.textContent = saved > 0 ? 'You save ' + vcdFmt(saved) : ''; badge.style.display = saved > 0 ? '' : 'none'; }
}

/* ── Count badges ────────────────────────────────── */
function vcdUpdateCount(n) {
  const countEl = vcd$('vcd-count');
  if (countEl) countEl.textContent = n;

  // Dawn .cart-count-bubble
  document.querySelectorAll('.cart-count-bubble').forEach(b => {
    b.querySelectorAll('span:not(.visually-hidden)').forEach(s => s.textContent = n);
    b.style.display = n > 0 ? '' : 'none';
  });

  // Custom header badges — create if missing
  ['desktop-cart-icon', 'mobile-cart-icon'].forEach(id => {
    const icon = vcd$(id);
    if (!icon) return;
    let badge = icon.querySelector('.cart-count');
    if (n > 0 && !badge) {
      badge = document.createElement('span');
      badge.className = 'cart-count';
      icon.appendChild(badge);
    }
    if (badge) { badge.textContent = n; badge.style.display = n > 0 ? '' : 'none'; }
  });
}

/* ── Reward bar ─────────────────────────────────── */
function vcdRewardBar(total) {
  const max       = VCD_TIERS[VCD_TIERS.length - 1].threshold;
  const pct       = Math.min(100, (total / max) * 100);
  const fill      = vcd$('vcd-progress-fill');
  const msg       = vcd$('vcd-reward-msg');
  const firstRun  = _prevRewardTotal < 0;
  const increased = !firstRun && total > _prevRewardTotal;

  if (fill) fill.style.width = pct + '%';

  // Progress percentage label ("72% complete")
  const pctEl = vcd$('vcd-reward-pct');
  if (pctEl) {
    if (pct >= 100)      pctEl.textContent = '100% ✓';
    else if (pct >= 1)   pctEl.textContent = Math.round(pct) + '% complete';
    else                 pctEl.textContent = '';
  }

  // Leading-edge tip — created once, transitions in sync with fill width
  let tip = vcd$('vcd-progress-tip');
  if (!tip && fill) {
    tip = document.createElement('div');
    tip.id = 'vcd-progress-tip';
    tip.className = 'vcd-progress-tip';
    fill.parentElement.appendChild(tip);
  }
  if (tip) {
    tip.style.left = pct + '%';
    tip.style.opacity = pct > 0.5 ? '1' : '0';
  }

  const nextIdx = VCD_TIERS.findIndex(t => total < t.threshold);

  VCD_TIERS.forEach((tier, i) => {
    const dot    = vcd$('vcd-ms-' + i);
    if (!dot) return;
    const circle  = dot.querySelector('.vcd-ms-circle');
    const hit     = total >= tier.threshold;
    const wasHit  = dot.classList.contains('reached');
    const isNext  = i === nextIdx;

    dot.classList.toggle('reached',  hit);
    dot.classList.toggle('next',     !hit && isNext);
    dot.classList.toggle('upcoming', !hit && !isNext);

    if (circle) circle.textContent = hit ? '✓' : '';

    // Celebrate only when we actually crossed this threshold (not on initial render)
    if (hit && !wasHit && increased) {
      dot.classList.add('celebrating');
      vcdShowToast('🎉 ' + tier.reward + ' unlocked!');
      setTimeout(() => dot.classList.remove('celebrating'), 600);
    }
  });

  _prevRewardTotal = total;

  if (!msg) return;

  const next = nextIdx >= 0 ? VCD_TIERS[nextIdx] : null;
  if (!next) {
    msg.innerHTML = '<strong>🎉 All rewards unlocked!</strong>';
    return;
  }

  const remaining = next.threshold - total;

  // Progress within the segment leading to the next tier
  const prevThreshold = nextIdx > 0 ? VCD_TIERS[nextIdx - 1].threshold : 0;
  const segPct = Math.round(((total - prevThreshold) / (next.threshold - prevThreshold)) * 100);

  if (remaining <= 20000) {
    // ≤ ₹200 — maximum urgency: "add one more item" framing
    msg.innerHTML = `Add one more item to unlock <strong>${vcdEsc(next.reward)}</strong> — only <strong>${vcdFmt(remaining)}</strong> left!`;
  } else if (remaining <= 50000) {
    // ₹200–₹500 — "almost there" urgency
    msg.innerHTML = `Almost there! <strong>${vcdFmt(remaining)}</strong> more unlocks <strong>${vcdEsc(next.reward)}</strong>`;
  } else if (segPct >= 60) {
    // Well into this segment — progress percentage feels motivating
    msg.innerHTML = `You're <strong>${segPct}% of the way</strong> to your next reward — only <strong>${vcdFmt(remaining)}</strong> to go`;
  } else {
    // General — exact gap, clean and clear
    msg.innerHTML = `Only <strong>${vcdFmt(remaining)}</strong> away from <strong>${vcdEsc(next.reward)}</strong>`;
  }
}

/* ── Reward toast ────────────────────────────────── */
function vcdShowToast(text) {
  const rewardEl = document.querySelector('.vcd-reward');
  if (!rewardEl) return;

  let toast = vcd$('vcd-reward-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'vcd-reward-toast';
    toast.className = 'vcd-reward-toast';
    rewardEl.appendChild(toast);
  }

  toast.textContent = text;
  toast.classList.add('show');

  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('show'), 3200);
}

/* ── Cross-sell helpers ──────────────────────────── */
function vcdNormPrice(p) {
  // Handles both integer paise (249900) and string rupees ("2499.00")
  if (typeof p === 'string' && p.includes('.')) return Math.round(parseFloat(p) * 100);
  return parseInt(p, 10) || 0;
}

function vcdProductImg(p) {
  const fi = p.featured_image;
  if (fi) return typeof fi === 'string' ? fi : (fi.src || '');
  const imgs = p.images;
  if (imgs && imgs.length) {
    const img = imgs[0];
    return typeof img === 'string' ? img : (img.src || '');
  }
  return '';
}

/* ── Cross-sell — recommendations carousel ──────── */
function vcdCrossSell(cart) {
  const box  = vcd$('vcd-crosssell');
  const slot = vcd$('vcd-ccs-items');
  if (!box || !slot) return;

  if (!cart.items.length) { box.style.display = 'none'; return; }

  const inCartIds = new Set(cart.items.map(i => i.product_id));
  const pid       = cart.items[0].product_id;

  fetch('/recommendations/products.json?product_id=' + pid + '&limit=8&intent=related')
    .then(r => r.json())
    .then(data => {
      const recs = (data.products || [])
        .filter(p => !inCartIds.has(p.id))
        .slice(0, 3);
      if (!recs.length) { box.style.display = 'none'; return; }
      vcdRenderCarousel(recs, slot);
      box.style.display = 'block';
    })
    .catch(() => vcdCrossSellHandles(cart, box, slot));
}

function vcdCrossSellHandles(cart, box, slot) {
  const inCart = new Set(cart.items.map(i => i.handle));
  const handle = VCD_CROSS_SELL.find(h => !inCart.has(h));
  if (!handle) { box.style.display = 'none'; return; }

  fetch('/products/' + handle + '.js')
    .then(r => r.ok ? r.json() : Promise.reject())
    .then(p => { vcdRenderCarousel([p], slot); box.style.display = 'block'; })
    .catch(() => { box.style.display = 'none'; });
}

/* ── Render carousel (1-4 product cards) ────────── */
function vcdRenderCarousel(products, slot) {
  slot.innerHTML = '';

  products.forEach(p => {
    const v0        = p.variants[0];
    const price     = vcdNormPrice(v0.price);
    const compare   = v0.compare_at_price ? vcdNormPrice(v0.compare_at_price) : 0;
    const imgSrc    = vcdProductImg(p);
    const wasTag    = compare > price
      ? `<span class="vcd-ccs-card-was">${vcdFmt(compare)}</span>` : '';
    // Only a single "Default Title" variant — no real options to pick, so
    // skip the size dropdown entirely and add directly, same as the card grid.
    const singleVariant = p.variants.length === 1;
    const opts = p.variants.map(v =>
      `<option value="${v.id}"${v.available ? '' : ' disabled'}>${vcdEsc(v.title)}${v.available ? '' : ' - Sold out'}</option>`
    ).join('');

    const card = document.createElement('div');
    card.className = 'vcd-ccs-card';
    card.innerHTML = `
      <img class="vcd-ccs-card-img"
           src="${vcdEsc(vcdImg(imgSrc, 260))}"
           alt="${vcdEsc(p.title)}"
           loading="lazy"
           onerror="this.style.opacity='0'">
      <div class="vcd-ccs-card-body">
        <div class="vcd-ccs-card-name">${vcdEsc(p.title)}</div>
        <div class="vcd-ccs-card-prices">
          <span class="vcd-ccs-card-price">${vcdFmt(price)}</span>${wasTag}
        </div>
        ${singleVariant ? '' : `<select class="vcd-ccs-card-sel">
          <option value="">Size</option>${opts}
        </select>`}
        <button class="vcd-ccs-card-btn"${singleVariant && !v0.available ? ' disabled' : ''}>${singleVariant && !v0.available ? 'Sold out' : 'Add'}</button>
      </div>`;

    const btn = card.querySelector('.vcd-ccs-card-btn');
    const sel = card.querySelector('.vcd-ccs-card-sel');

    btn.addEventListener('click', function () {
      const vid = singleVariant ? String(v0.id) : (sel ? sel.value : '');
      if (!vid) { if (sel) sel.style.borderColor = 'var(--vcd-rust)'; return; }
      btn.disabled = true;
      btn.textContent = '…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(vid, 10), quantity: 1 }),
      })
        .then(r => r.json())
        .then(() => {
          btn.textContent = '✓';
          btn.classList.add('added');
          vcdFetch();
          setTimeout(() => {
            btn.textContent = 'Add';
            btn.classList.remove('added');
            btn.disabled = false;
          }, 2000);
        })
        .catch(() => { btn.textContent = 'Add'; btn.disabled = false; });
    });

    slot.appendChild(card);
  });
}

/* ── Discount ────────────────────────────────────── */
function vcdApplyDiscount() {
  const input   = vcd$('vcd-disc-input');
  const btn     = vcd$('vcd-disc-btn');
  const success = vcd$('vcd-disc-success');
  const link    = vcd$('vcd-checkout-link');
  if (!input) return;
  const code = input.value.trim().toUpperCase();
  if (!code) return;
  _discount = code;
  sessionStorage.setItem('vcd_discount', code);
  if (btn)     { btn.textContent = '✓ Applied'; btn.classList.add('applied'); }
  if (success) { success.textContent = '✓ Code ' + code + ' applied'; success.classList.add('show'); }
  if (link)    link.href = '/checkout?discount=' + encodeURIComponent(code);
}

function vcdRestoreDiscount() {
  if (!_discount) return;
  const input   = vcd$('vcd-disc-input');
  const btn     = vcd$('vcd-disc-btn');
  const success = vcd$('vcd-disc-success');
  const link    = vcd$('vcd-checkout-link');
  if (input)   input.value = _discount;
  if (btn)     { btn.textContent = '✓ Applied'; btn.classList.add('applied'); }
  if (success) { success.textContent = '✓ Code ' + _discount + ' applied'; success.classList.add('show'); }
  if (link)    link.href = '/checkout?discount=' + encodeURIComponent(_discount);
}

/* ── Event wiring ────────────────────────────────── */
function vcdBind() {
  const overlay   = vcd$('varali-cart-overlay');
  const closeBtn  = vcd$('vcd-close');
  const continueB = vcd$('vcd-continue');
  const browseB   = vcd$('vcd-browse-btn');
  const discInput = vcd$('vcd-disc-input');
  const discBtn   = vcd$('vcd-disc-btn');

  if (overlay)   overlay.addEventListener('click', vcdClose);
  if (closeBtn)  closeBtn.addEventListener('click', vcdClose);
  if (continueB) continueB.addEventListener('click', vcdClose);
  if (browseB)   browseB.addEventListener('click', () => { vcdClose(); window.location.href = '/collections/all'; });

  if (discBtn)   discBtn.addEventListener('click', vcdApplyDiscount);
  if (discInput) discInput.addEventListener('keydown', e => e.key === 'Enter' && vcdApplyDiscount());

  document.addEventListener('keydown', e => { if (e.key === 'Escape' && _open) vcdClose(); });
}

/* ── Cart icon / link intercept (capture phase) ─── */
// Uses document-level capture so it works even if elements are replaced/re-rendered
function vcdInterceptClicks() {
  document.addEventListener('click', e => {
    const target = e.target;

    // Cart icon buttons (#desktop-cart-icon, #mobile-cart-icon, #cart-icon-bubble)
    const icon = target.closest('#desktop-cart-icon, #mobile-cart-icon, #cart-icon-bubble');
    if (icon) {
      console.log('[Varali] cart icon clicked:', icon.id);
      e.preventDefault();
      e.stopPropagation();
      vcdOpen();
      return;
    }

    // Any <a href="/cart"> link
    const link = target.closest('a[href]');
    if (link) {
      const href = link.getAttribute('href');
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      if (href === '/cart' || href === cartUrl) {
        console.log('[Varali] /cart link intercepted');
        e.preventDefault();
        e.stopPropagation();
        vcdOpen();
      }
    }
  }, true); // capture phase — runs before any other handlers
}

/* ── ATC form submit helper ─────────────────────── */
function vcdSubmitForm(form) {
  const fd   = new FormData(form);
  const body = { quantity: 1 };
  for (const [k, v] of fd.entries()) {
    if (k === 'sections' || k === 'sections_url') continue;
    if (k.startsWith('properties[')) {
      if (!body.properties) body.properties = {};
      body.properties[k.replace(/^properties\[(.+)\]$/, '$1')] = v;
    } else {
      body[k] = v;
    }
  }
  if (body.id)       body.id       = parseInt(body.id, 10);
  if (body.quantity) body.quantity = parseInt(body.quantity, 10) || 1;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then(r => { if (!r.ok) return r.json().then(d => Promise.reject(d)); return r.json(); })
    .then(() => {
      history.pushState(null, '', window.location.href);
      vcdOpen();
    })
    .catch(err => { if (err && err.description) alert(err.description); });
}

/* ── Skip forms that have their own ATC handler ──── */
// #product-form is owned by custom-product.js (it validates the variant and
// opens the drawer itself). Letting our interceptors fire would double-add and
// skip that validation, so we leave it alone here.
function vcdSkipForm(form) {
  return !form || form.id === 'product-form' || form.hasAttribute('data-vcd-skip');
}

/* ── ATC form intercept ─────────────────────────── */
function vcdInterceptATC() {

  // ── Layer 1: document-level submit capture ────────
  document.addEventListener('submit', function(e) {
    const form = e.target;
    if (!form || !form.matches('[action*="/cart/add"]')) return;
    if (vcdSkipForm(form)) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    vcdSubmitForm(form);
  }, { capture: true });

  // ── Layer 2: MutationObserver — bind directly on each
  //    ATC form the moment it appears, before Dawn touches it
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(m) {
      m.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        const forms = node.matches('form[action*="/cart/add"]')
          ? [node]
          : Array.prototype.slice.call(node.querySelectorAll('form[action*="/cart/add"]'));
        forms.forEach(function(form) {
          if (form._vcdBound || vcdSkipForm(form)) return;
          form._vcdBound = true;
          form.addEventListener('submit', function(e) {
            e.preventDefault();
            e.stopImmediatePropagation();
            vcdSubmitForm(form);
          }, { capture: true });
        });
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // ── Layer 3: bind all ATC forms already in the DOM ─
  document.querySelectorAll('form[action*="/cart/add"]').forEach(function(form) {
    if (form._vcdBound || vcdSkipForm(form)) return;
    form._vcdBound = true;
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      vcdSubmitForm(form);
    }, { capture: true });
  });

  // ── Layer 4: [name="add"] button click intercept ──
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[name="add"]');
    if (!btn) return;
    const form = btn.closest('form[action*="/cart/add"]');
    if (!form || vcdSkipForm(form)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    vcdSubmitForm(form);
  }, { capture: true });
}

/* ── Dawn proxy (prevents Dawn's product-form.js crashing) ─ */
try {
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class extends HTMLElement {
      renderContents() { vcdOpen(); }
      open()           { vcdOpen(); }
      close()          { vcdClose(); }
      getSectionsToRender()          { return []; }
      setActiveElement()             {}
      setHeaderCartIconAccessibility() {}
    });
  }
} catch (e) { console.warn('[Varali] cart-drawer CE:', e); }

try {
  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', class extends HTMLElement {});
  }
} catch (e) {}

/* ── Globals (so inline scripts like the sticky bar can call these) ── */
window.vcdOpen        = vcdOpen;
window.vcdClose       = vcdClose;
window.vcdSubmitForm  = vcdSubmitForm;

/* ── Init ────────────────────────────────────────── */
function vcdInit() {
  console.log('[Varali] init — drawer:', !!vcd$('varali-cart-drawer'));

  // Flush any form submission queued before this script loaded
  if (window.__vcdPendingForm) {
    vcdSubmitForm(window.__vcdPendingForm);
    window.__vcdPendingForm = null;
  }

  vcdForceFront();     // relocate drawer to <body> + force positioning on load
  vcdInterceptATC();   // first — layers 2 & 3 need the DOM ready
  vcdBind();
  vcdInterceptClicks();

  if (sessionStorage.getItem('vcd_back')) {
    sessionStorage.removeItem('vcd_back');
    setTimeout(vcdOpen, 350);
  }
}

document.addEventListener('DOMContentLoaded', vcdInit);

// If DOM is already parsed (script was not deferred / injected late),
// run the intercept layers immediately so no form is missed
if (document.readyState !== 'loading') {
  vcdInterceptATC();
}
